import { Box, Button, Grid, IconButton, MenuItem, Tooltip, Typography } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import filter from 'lodash/filter'
import find from 'lodash/find'
import get from 'lodash/get'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'
import map from 'lodash/map'
import moment from 'moment'
import { useEffect, useState } from 'react'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import FilterSelect from 'component/Select/FilterSelect'
import DoctorFee from 'features/dashboard/Dashboard/doctorFee'
import HightSaleProduct from 'features/dashboard/Dashboard/hightSaleProduct'
import LowSaleProduct from 'features/dashboard/Dashboard/lowSaleProduct'
import PaymentMethod from 'features/dashboard/Dashboard/paymentMethod'
import SalesOperative from 'features/dashboard/Dashboard/salesOperative'
import SalesProduct from 'features/dashboard/Dashboard/salesProduct'
import Summary from 'features/dashboard/Dashboard/summary'
import TotalExpenses from 'features/dashboard/Dashboard/totalExpenses'
import HightSaleOperative from './Dashboard/hightSaleOperative'
import OperativeType from './Dashboard/operativeType'

/** CONSTANTS */
import { KEY_STORAGE } from 'constants/common'
import { colors } from 'constants/theme'
import { getSecureItem, setSecureItem } from 'utils/app.utils'

/** API */
import ClinicApi, { BranchInterface } from 'api/master/clinic.api'

/** STYLE */
import * as UseStyled from 'features/dashboard/useStyled'
import { canManageTargets, hasAnyExecutiveTab } from 'features/executive/permission-store'
import { routeName } from 'routes/routes-name'
import { useHistory } from 'utils/router-compat'

export interface CardSalesProps {
  branches?: BranchInterface[]
  branch?: (string | number)[]
  rangeDate?: string[]
  handleChangeDashboard?: (key: string, value: any) => void
}

export default function Dashboard() {
  const { t } = useTranslation()
  const history = useHistory()
  const showExecutiveHeader = hasAnyExecutiveTab()
  const canManage = canManageTargets()

  const dashboardMain = getSecureItem(KEY_STORAGE.DASHBOARD)
  const dashboardCurrent = dashboardMain ? JSON.parse(dashboardMain) : {}

  const [dataDashboard, setDataDashboard] = useState<any>(dashboardCurrent || {})

  const [branches, setBranches] = useState<BranchInterface[]>([])
  const [branch, setBranch] = useState<(string | number)[]>(['ALL'])
  const [rangeDate, setRangeDate] = useState([moment().format('YYYY-MM-01'), moment().format('YYYY-MM-DD')])

  const handleChangeDashboard = (key: string, value: any) => {
    const [firstKey, secKey] = key?.split(',')
    if (firstKey === "branch" || firstKey === "rangeDate") {
      const updatedData = { ...dataDashboard };
      if (firstKey === "branch") {
        Object.keys(updatedData).forEach(k => {
          if (updatedData[k].branch) {
            updatedData[k].branch = value;
          }
        });
      }
      if (firstKey === "rangeDate") {
        Object.keys(updatedData).forEach(k => {
          if (updatedData[k].rangeDate) {
            updatedData[k].rangeDate = value;
          }
        });
      }

      setDataDashboard({ ...updatedData, [firstKey]: value });
    } else if (!secKey) {
      setDataDashboard({ ...dataDashboard, [firstKey]: value });
    } else {
      setDataDashboard({
        ...dataDashboard,
        [firstKey]: { ...dataDashboard[firstKey], [secKey]: value },
      });
    }
  }

  useEffect(() => {
    setSecureItem(KEY_STORAGE.DASHBOARD, JSON.stringify(dataDashboard))
  }, [dataDashboard])

  useEffect(() => {
    setBranch(dataDashboard?.branch || ['ALL'])
    setRangeDate(dataDashboard?.rangeDate || [moment().format('YYYY-MM-01'), moment().format('YYYY-MM-DD')])
  }, [dataDashboard?.branch, dataDashboard?.rangeDate])

  const loadDefaultData = async () => {
    await ClinicApi.findAllBranches().then(({ data }) => setBranches(data))
  }

  const handleChangeSelectBranch = (e: any) => {
    handleChangeDashboard("branch", handleChangeFilterSelect(e))
    // setBranch(handleChangeFilterSelect(e))
  }

  useEffect(() => {
    loadDefaultData()
  }, [])

  return (
    <div className="px-3 px-md-4">
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: 'background.paper',
        }}
      >
        {showExecutiveHeader && (
          <Box
            className="hideInPrint"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              // Wrap so title block + toolbar stack vertically on mobile.
              flexWrap: 'wrap',
              gap: 1.5,
              px: { xs: 2, sm: 3 },
              py: 1.5,
              minHeight: 64,
              // Navy bottom border matches TabHeader so the page header
              // reads as part of the same "executive report" frame across
              // every tab — including the legacy Dashboard view.
              borderBottom: `2px solid ${colors.themeMainColor}`,
            }}
          >
            {/* This is the legacy report set, not the executive "แดชบอร์ด"
                tab (that lives at /executive/overview). Say so in the title
                and explain why its numbers can differ, instead of showing
                the executive tab bar here (MN-2609-1165 TC-06). */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: colors.themeMainColor,
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('EXECUTIVE.LEGACY_DASHBOARD.TITLE')}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textLightGray }}>
                {t('EXECUTIVE.LEGACY_DASHBOARD.NOTE')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => history.push(routeName.executiveOverview)}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', color: colors.themeSecondColor, borderColor: colors.themeSecondColor }}
              >
                {t('EXECUTIVE.LEGACY_DASHBOARD.GO_EXECUTIVE')}
              </Button>
            {canManage && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `1px solid ${colors.lightGray}`,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    '& .MuiIconButton-root': { borderRadius: 0, color: colors.themeMainColor },
                  }}
                >
                  <Tooltip title={t('EXECUTIVE.HEADER.TARGET_SETUP_TOOLTIP') || ''}>
                    <IconButton
                      size="small"
                      onClick={() => history.push(routeName.dashboardTargets)}
                      aria-label="dashboard target settings"
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
            )}
            </Box>
          </Box>
        )}
      </Box>
      {/* Filter row chrome matches ExecutiveFilterBar (py: 2 + divider
          underneath) so the page still reads like the other dashboards. */}
      <Box sx={{ py: 2, borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={4} lg={3} xl={2} className="w-100">
            <FilterSelect
            multiple
            style={{ height: '40px' }}
            label={t('')}
            labelId="label-branch"
            selectId="select-branch"
            onchange={handleChangeSelectBranch}
            renderValue={() => `${t('DASHBOARD.FILTER.BRANCH')}: ${renderFilterSelect('branch', branches, branch) === 'ALL' ? t('STATUS.ALL') : renderFilterSelect('branch', branches, branch)}`}
            value={branch}
            options={[
              <MenuItem
                key="ALL"
                value="ALL"
                sx={{
                  '&.MuiMenuItem-root.Mui-selected': {
                    pointerEvents: includes(branch, 'ALL') ? `none` : 'auto',
                    backgroundColor: includes(branch, 'ALL') ? `${colors.themeSecondColor06} !important` : 'transparent',
                    color: includes(branch, 'ALL') ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                  }
                }}
              >
                {t('STATUS.ALL')}
              </MenuItem>,
              map(branches, (val: BranchInterface, indexBranch: number) => {
                return (
                  <MenuItem
                    key={indexBranch}
                    value={val.branchId}
                    sx={{
                      '&.MuiMenuItem-root.Mui-selected': {
                        backgroundColor: includes(branch, val.branchId) ? `${colors.themeSecondColor06} !important` : 'transparent',
                        color: includes(branch, val.branchId) ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                      }
                    }}
                  >
                    {val.branchName}
                  </MenuItem>
                )
              })
            ]}
            formControlClass={'not-max-width'}
            classesOption="style-select-doctors"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4} lg={3} xl={2} className="w-100">
          <InputSecondNewRangePicker
            inputHeight={40}
            value={rangeDate}
            onchange={(val) => {
              if (isEmpty(val)) {
                handleChangeDashboard("rangeDate", emptyRangePicker)
                // setRangeDate(emptyRangePicker)
              }
              else {
                handleChangeDashboard("rangeDate", [moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                //  setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }
            }}
            allowClear
            onClear={() => handleChangeDashboard("rangeDate", emptyRangePicker)}
          />
        </Grid>
        </Grid>
      </Box>
      <Summary branch={branch} rangeDate={rangeDate} />
      <Grid container spacing={2} className="mb-4">
        <Grid item className="w-100">
          <UseStyled.Title type="header">{t('DASHBOARD.HEADER.SALE')}</UseStyled.Title>
        </Grid>
        <Grid item lg={6} className="w-100">
          <SalesProduct branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
        <Grid item lg={6} className="w-100">
          <SalesOperative branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
        <Grid item lg={6} className="w-100">
          <DoctorFee rangeDate={rangeDate} branch={branch} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item lg={6} className="w-100 mb-4">
          <Box className="d-flex flex-column h-100">
            <UseStyled.Title type="header">{t('DASHBOARD.HEADER.TOTAL_EXPENSES')}</UseStyled.Title>
            <Box className="pt-3 h-100">
              <TotalExpenses branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
            </Box>
          </Box>
        </Grid>
        <Grid item lg={6} className="w-100 mb-4">
          <Box className="d-flex flex-column h-100">
            <UseStyled.Title type="header">{t('DASHBOARD.HEADER.PAYMENT')}</UseStyled.Title>
            <Box className="pt-3 h-100">
              <PaymentMethod branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} className="mb-4">
        <Grid item className="w-100">
          <UseStyled.Title type="header">{t('DASHBOARD.HEADER.OPERATIVE')}</UseStyled.Title>
        </Grid>
        <Grid item lg={6} className="w-100">
          <OperativeType branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
        <Grid item lg={6} className="w-100">
          <HightSaleOperative branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
      </Grid>
      <Grid container spacing={2} className="mb-4">
        <Grid item className="w-100">
          <UseStyled.Title type="header">{t('DASHBOARD.HEADER.WAREHOUSE')}</UseStyled.Title>
        </Grid>
        <Grid item lg={6} className="w-100">
          <LowSaleProduct branches={branches} branch={branch} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
        <Grid item lg={6} className="w-100">
          <HightSaleProduct branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
      </Grid>
    </div>
  )
}

export const handleChangeFilterSelect = (e: any) => {
  const value = e.target.value
  if (last(value) === 'ALL' || isEmpty(value)) return ['ALL']
  else
    return filter(value, (v) => {
      return !includes(v, 'ALL')
    })
}

export const renderFilterSelect = (type: 'branch' | 'dentist' | 'item' | 'operativeType', data: any, value: (string | number)[]) => {
  if (last(value) === 'ALL') return 'ALL'
  else {
    const items: string[] = []
    map(value, (v) => {
      if (type === 'branch') items.push(get(find(data, { branchId: v }), 'branchName'))
      else if (type === 'dentist') items.push(get(find(data, { dentistId: v }), 'dentistFullname'))
      else if (type === 'operativeType') items.push(get(find(data, { itemTypeId: v }), 'itemTypeName'))
      else items.push(get(find(data, { operativeId: v }), 'operativeName'))
    })
    return items.toString().replaceAll(',', ', ')
  }
}
